import { CdkDragDrop } from '@angular/cdk/drag-drop';
import { Component, Inject, OnInit, QueryList, ViewChildren } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatTableDataSource } from '@angular/material/table';
import { Subject, takeUntil } from 'rxjs';
import { LabpartnerService } from 'src/app/services/labpartner.service';
import {
  ElasticResponseItem,
  ICCassetteGroup,
  ICCassetteInfo,
  ICExperiment,
  ICLegendChangeType,
  ICLegendData,
  ICLegendDataIteration,
} from 'src/app/services/labpartner.service.model';
import { UserAccountService } from 'src/app/services/user-account.service';
import { DialogService } from 'src/app/shared/dialog.service';
import { NotificationService } from 'src/app/shared/notification.service';
import { BaseComponent } from 'src/app/support/base.component';
import { ICRoundService } from '../ic-round.service';
import { ICImagesDialogComponent } from '../images-dialog/ic-images-dialog.component';
import { ICExcludeIncludeDialogComponent } from './exclude-include-dialog/ic-exclude-include-dialog.component';

export interface ICAdjustLegendComponentData {
  legendData: ICLegendData[];
  testResults: ElasticResponseItem[];
  legendDataIteration: ICLegendDataIteration[];
  cassetteInfo?: ICCassetteInfo[];
  icExperimentId: number;
  experiment: ICExperiment;
}

@Component({
  selector: 'ic-adjust-legend',
  templateUrl: './ic-adjust-legend.component.html',
  styleUrls: ['./ic-adjust-legend.component.scss'],
  standalone: false,
})
export class ICAdjustLegendComponent extends BaseComponent implements OnInit {
  experimentId: number = 0;

  experiment: ICExperiment = {} as ICExperiment;

  legendData?: ICLegendData[];

  dataSource: MatTableDataSource<ICLegendData> = new MatTableDataSource<ICLegendData>();

  displayedColumns: string[] = ['legendDataId', 'name', 'cassettes'];

  testResults: ElasticResponseItem[] = [];

  legendDataIterations: ICLegendDataIteration[] = [];

  cassetteInfo: ICCassetteInfo[] = [];

  newCassetteInfo: ICCassetteInfo[] = [];

  icExperimentId: number = 0;

  rounds: number[] = [];

  selectedRound: number = 0;

  @ViewChildren('dropListRef') dropLists!: QueryList<any>;
  connectedDropLists: any[] = [];

  currentRoles: string[] = [];

  cassettes: ICCassetteGroup[] = [];

  cassetteLiveNotesCountMap?: { [key: number]: number } = {};

  private readonly _destroying$ = new Subject<void>();

  constructor(
    public dialogRef: MatDialogRef<ICAdjustLegendComponent>,
    @Inject(MAT_DIALOG_DATA) public data: ICAdjustLegendComponentData,
    private dialog: MatDialog,
    public apiService: LabpartnerService,
    private notificationService: NotificationService,
    private dialogService: DialogService,
    public accountService: UserAccountService,
    public icRoundService: ICRoundService,
  ) {
    super();
    this.legendData = data.legendData;
    this.legendDataIterations = data.legendDataIteration;
    this.testResults = data.testResults;
    this.cassetteInfo = data.cassetteInfo || [];
    this.icExperimentId = data.icExperimentId;
    this.experimentId = data.experiment.experimentId;
    this.experiment = data.experiment;
    console.log('ICAdjustLegendComponent initialized with data:', this.data);

    // Ensure each legendDataItem has a cassettes array
    this.legendData.forEach(legendDataItem => {
      legendDataItem.cassettes = [];
    });

    // Initialize rounds from test results
    const roundNumbers = new Set<number>();
    this.testResults.forEach(tr => {
      if (tr.imageCapture && typeof tr.imageCapture.roundNumber === 'number') {
        roundNumbers.add(tr.imageCapture.roundNumber);
      }
    });
    this.rounds = Array.from(roundNumbers).sort((a, b) => a - b);
    if (this.rounds.length > 0) {
      this.selectedRound = this.rounds[this.rounds.length - 1]; // Default to the last round
    }
    if (!this.rounds.includes(0)) {
      this.rounds.unshift(0);
    }

    // Group testResults under legendDataItem > cassette > testResults
    this.testResults.forEach(testResult => {
      const iteration = this.legendDataIterations.find(
        iteration =>
          iteration.casseteNumber === testResult.imageCapture.casseteNumber.split('.')[0] &&
          testResult.imageCapture.roundNumber === iteration.roundNumber,
      );
      if (!iteration) {
        return;
      }

      const legendDataItem = this.legendData?.find(
        item => item.legendDataId === iteration.legendDataId,
      );
      if (!legendDataItem) {
        return;
      }

      // Ensure the cassette group exists in the legendDataItem
      let cassette = this.cassettes.find(
        c => c.casseteNumber === testResult.imageCapture.casseteNumber,
      );
      // If cassette does not exist, create it
      if (!cassette) {
        cassette = {
          casseteNumber: testResult.imageCapture.casseteNumber,
          testResults: [testResult],
          legendDataId: legendDataItem.legendDataId,
          cassetteInfo: {
            legendDataId: legendDataItem.legendDataId,
          } as ICCassetteInfo,
          isMoved: false,
          isExtra:
            testResult.imageCapture.casseteNumber.split('.')[1].length > 1 &&
            testResult.imageCapture.casseteNumber.split('.')[1] != '1',
          isExcluded: false,
          show: true,
          roundNumber: testResult.imageCapture.roundNumber,
        };
        this.cassettes.push(cassette);
      } else {
        // If cassette exists, add the testResult to it
        cassette.testResults.push(testResult);
        return;
      }

      const cassetteInfo = this.cassetteInfo.find(
        c => c.casseteNumber === testResult.imageCapture.casseteNumber,
      );

      if (cassetteInfo) {
        cassette.isExcluded = !!cassetteInfo.isExcluded;
        cassette.cassetteInfo.legendDataId = cassetteInfo.legendDataId; // Update legendDataId in cassetteInfo
        if (cassette.legendDataId !== cassette.cassetteInfo.legendDataId) {
          cassette.isMoved = true;
        }
      }
    });

    this.cassettes.forEach(cassette => {
      // Find the corresponding legendDataItem
      const legendDataItem = this.legendData?.find(
        item => item.legendDataId === cassette.cassetteInfo.legendDataId,
      );
      if (legendDataItem) {
        // Add the cassette to the legendDataItem's cassettes array
        legendDataItem.cassettes.push(cassette);
      }
    });

    // Find cassettes with live notes
    this.subscription.add(
      this.apiService
        .getNotesList('ExperimentId', this.experimentId, 'CassetteLiveNote')
        .subscribe(liveNotes => {
          if (liveNotes) {
            liveNotes.forEach((note: any) => {
              const casseteNumber = this.decodeCassetteId(note.objectId);
              const cassette = this.cassettes.find(c => c.casseteNumber === casseteNumber);
              if (cassette) {
                cassette.hasLiveNote = true;
              }
            });
          }
        }),
    );

    // Sort cassettes within each legendDataItem by casseteNumber
    this.legendData.forEach(legendDataItem => {
      if (legendDataItem.cassettes && legendDataItem.cassettes.length > 0) {
        legendDataItem.cassettes.sort((a, b) => {
          return a.casseteNumber.localeCompare(b.casseteNumber);
        });
      }
    });

    // Set data source
    this.dataSource = new MatTableDataSource<ICLegendData>(this.legendData);

    // Initialize the test results with show property
    this.showAllTestResults();

    this.onRoundChange({ value: this.selectedRound });
  }

  ngOnInit(): void {
    this.accountService.currentRoles$.pipe(takeUntil(this._destroying$)).subscribe(roles => {
      this.currentRoles = roles;
    });
  }

  ngAfterViewInit() {
    // Usa setTimeout para esperar a que todo el DOM esté listo
    setTimeout(() => {
      this.connectedDropLists = this.dropLists.toArray().map(ref => ref);
    });
  }

  protected ngOnDestroyInternal(): void {}

  save(): void {
    if (this.newCassetteInfo.length > 0) {
      this.dialogService
        .openConfirmDialog('All previous changes will be saved. Are you sure to continue?', {
          width: '400px',
          isOk: false,
        })
        .afterClosed()
        .subscribe(result => {
          if (result) {
            // Extract testResultIds for affected cassettes
            const affectedCassetteNumbers = this.newCassetteInfo.map(c => c.casseteNumber).filter((cn): cn is string => !!cn);
            const testResultIds = this.testResults
              .filter(tr => {
                const cassetteNumber = tr.imageCapture?.casseteNumber;
                if (!cassetteNumber) return false;
                // Compare only the part before the dot (like in the web client matching logic)
                const cassetteNumberBase = cassetteNumber.split('.')[0];
                return affectedCassetteNumbers.some(cn => cn && cassetteNumberBase === cn.split('.')[0]);
              })
              .map(tr => tr.testResultId)
              .filter((id): id is string => !!id)
              .filter((id, index, self) => self.indexOf(id) === index); // Remove duplicates

            this.subscription.add(
              this.apiService.icAdjustLegends(this.icExperimentId, this.newCassetteInfo, testResultIds).subscribe({
                next: () => {
                  this.notificationService.success('Cassette info updated successfully');
                  this.dialogRef.close(this.testResults);
                },
                error: error => {
                  console.error('Error updating cassette info:', error);
                },
              }),
            );
          } else {
            this.notificationService.warn('Changes not saved');
          }
        });
    } else {
      this.dialogService
        .openConfirmDialog('No changes to save. Are you sure to continue?', {
          width: '400px',
          isOk: false,
        })
        .afterClosed()
        .subscribe(result => {
          if (result) {
            this.dialogRef.close(this.testResults);
          }
        });
    }
  }

  cancel(): void {
    if (this.newCassetteInfo.length > 0) {
      this.dialogService
        .openConfirmDialog('All previous changes will be discarded. Are you sure to continue?', {
          width: '400px',
          isOk: false,
        })
        .afterClosed()
        .subscribe(result => {
          if (result) {
            this.dialogRef.close(null);
          }
        });
    } else {
      this.notificationService.warn('No changes to cancel');
      this.dialogRef.close(null);
    }
  }

  showImages(cassetteGroup: ICCassetteGroup): void {
    this.dialog
      .open(ICImagesDialogComponent, {
        width: '80%',
        height: '80%',
        data: {
          cassetteGroup: cassetteGroup,
        },
      })
      .afterClosed()
      .subscribe(() => {});
  }

  add(cassette: ICCassetteGroup): void {
    this.dialog
      .open(ICExcludeIncludeDialogComponent, {
        width: '800px',
        disableClose: true,
        data: { changeType: ICLegendChangeType.Include },
      })
      .afterClosed()
      .subscribe(reason => {
        if (reason !== null) {
          cassette.isExcluded = false;
          const legendDataId = cassette.legendDataId;
          this.newCassetteInfo.push({
            icExperimentId: this.icExperimentId,
            legendDataId: legendDataId,
            casseteNumber: cassette.casseteNumber,
            isExcluded: false,
            changeType: ICLegendChangeType.Include,
            reason: reason,
          });
        }
      });
  }

  remove(cassette: ICCassetteGroup): void {
    this.dialog
      .open(ICExcludeIncludeDialogComponent, {
        width: '800px',
        disableClose: true,
        data: { changeType: ICLegendChangeType.Exclude },
      })
      .afterClosed()
      .subscribe(reason => {
        if (reason !== null) {
          cassette.isExcluded = true;
          const legendDataId = cassette.legendDataId;
          this.newCassetteInfo.push({
            icExperimentId: this.icExperimentId,
            legendDataId: legendDataId,
            casseteNumber: cassette.casseteNumber,
            isExcluded: true,
            changeType: ICLegendChangeType.Exclude,
            reason: reason,
          });
        }
      });
  }

  // Returns an array of IDs for connected drop lists
  getConnectedDropListsIds(): string[] {
    return this.dataSource.data.map((_, index) => `cdk-drop-list-${index}`);
  }

  onRoundChange(event: any): void {
    console.log('Selected round:', event.value);
    const selectedRound = this.selectedRound;

    this.dataSource.data.forEach(legendDataItem => {
      if (!legendDataItem.cassettes) {
        return;
      }

      legendDataItem.cassettes.forEach(cassette => {
        if (selectedRound === 0) {
          cassette.show = true; // Show all
        } else {
          cassette.show = cassette.roundNumber === selectedRound;
        }
      });
    });

    // Trigger change detection
    this.dataSource.data = [...this.dataSource.data];
  }

  showAllTestResults(): void {
    this.dataSource.data.forEach(legendDataItem => {
      if (!legendDataItem.cassettes) {
        return;
      }

      legendDataItem.cassettes.forEach(cassette => {
        cassette.show = true; // Show all cassettes
      });
    });

    // Trigger change detection
    this.dataSource.data = [...this.dataSource.data];
  }

  // Returns the ID for a specific drop list based on its index
  dropListId(index: number): string {
    return `cdk-drop-list-${index}`;
  }

  onDrop(event: CdkDragDrop<ICCassetteGroup[]>, targetLegendIndex: number): void {
    // Find the source legend index by locating the dropped cassette in one of the legends
    const sourceLegendIndex = this.dataSource.data.findIndex(row =>
      row.cassettes?.some(c => c === event.item.data),
    );

    if (sourceLegendIndex === -1) {
      console.error('Source legend not found');
      return;
    }

    const sourceLegend = this.dataSource.data[sourceLegendIndex];
    const targetLegend = this.dataSource.data[targetLegendIndex];
    const cassetteToMove = event.item.data as ICCassetteGroup;

    // Find the index of the cassette within the source legend
    const sourceCassetteIndex = sourceLegend.cassettes.findIndex(c => c === cassetteToMove);
    if (sourceCassetteIndex === -1) {
      console.error('Cassette group not found in source legend');
      return;
    }

    const isSameLegend = sourceLegend === targetLegend;

    // Open confirmation dialog before moving the cassette
    this.dialog
      .open(ICExcludeIncludeDialogComponent, {
        width: '800px',
        disableClose: true,
        data: {
          changeType: ICLegendChangeType.Move,
          cassette: cassetteToMove.casseteNumber,
          currentLegend: `${sourceLegend.legendDataId} - ${sourceLegend.name}`,
          newLegend: `${targetLegend.legendDataId} - ${targetLegend.name}`,
        },
      })
      .afterClosed()
      .subscribe(reason => {
        if (reason !== null) {
          if (!isSameLegend) {
            // Remove the cassette from the source legend
            sourceLegend.cassettes.splice(sourceCassetteIndex, 1);

            // Update the cassette group to reflect the new legend
            cassetteToMove.legendDataId = targetLegend.legendDataId;

            // Update legendDataId for each test result in the cassette
            cassetteToMove.testResults.forEach(tr => {
              if (tr.cassetteInfo) {
                tr.cassetteInfo.legendDataId = targetLegend.legendDataId;
              }
            });

            // Insert the cassette into the target legend at the drop position
            targetLegend.cassettes.splice(event.currentIndex, 0, cassetteToMove);
          }

          // Track the move operation to be saved later
          this.newCassetteInfo.push({
            icExperimentId: this.icExperimentId,
            legendDataId: targetLegend.legendDataId,
            casseteNumber: cassetteToMove.casseteNumber,
            isExcluded: false,
            changeType: ICLegendChangeType.Move,
            reason: reason,
          });

          // Force table refresh to reflect the change in the UI
          this.dataSource.data = [...this.dataSource.data];

          // Order cassettes in both source and target legends by casseteNumber
          sourceLegend.cassettes.sort((a, b) => a.casseteNumber.localeCompare(b.casseteNumber));
          targetLegend.cassettes.sort((a, b) => a.casseteNumber.localeCompare(b.casseteNumber));
        }
      });
  }

  removeFirstVersion(cassetteNumber: string): string {
    // Split the cassette number by '.' and return the first part
    const parts = cassetteNumber.split('.');
    // If the cassette number has a '.' and the second part is '1', return the first part
    if (parts.length > 1 && parts[1] == '1') {
      return parts[0]; // Return the first part of the cassette number
    }
    return cassetteNumber; // If no '.' found, return the original cassette number
  }

  encodeCassetteId(cassetteNumber: string, factor = 1000): number {
    if (!cassetteNumber) {
      throw new Error('cassetteNumber is required');
    }
    const [left, right] = cassetteNumber.split('.');
    const base = parseInt(left, 10);
    if (Number.isNaN(base)) {
      throw new Error(`Invalid base: ${left}`);
    }
    const sub = right ? parseInt(right, 10) : 0;
    if (Number.isNaN(sub)) {
      throw new Error(`Invalid sub: ${right}`);
    }
    const id = base * factor + sub;
    // Check if the id exceeds the int32 range
    if (id > 2147483647) {
      throw new Error(`Encoded id ${id} exceeds int32 range`);
    }
    return id;
  }

  decodeCassetteId(id: number, factor = 1000): string {
    const base = Math.floor(id / factor);
    const sub = id % factor;
    return sub > 0
      ? `${base.toString().padStart(3, '0')}.${sub}`
      : `${base.toString().padStart(3, '0')}`;
  }
}
